import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { HelperService, LocalStorageService, SharedDataService } from '../services';
import { TOAST_STRATEGY, SKIP_LOADER } from '../services/http.service';
import {
  DEFAULT_INVALID_TOKEN_SERVER_RESPONSE,
  DEFAULT_INVALID_TOKEN_SIGNATURE_SERVER_RESPONSE,
  IMAGES_URL,
} from '@src/app/core/constants/system-list.constant';
import { StorageService } from '../services/storage.service';
import { ToastrService } from 'ngx-toastr';

import { ApiService } from '../services/api.service';
import { ErrorDialogService } from '../services/error-dialog.service';
import { environment } from '@src/environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private sharedDataService: SharedDataService,
    private localStorage: LocalStorageService,
    private storageService: StorageService,
    public toastrService: ToastrService,
    private helperService: HelperService,
    private apiService: ApiService,
    private errorService: ErrorDialogService
  ) { }

  // 👇 Correct signature
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Global loader: show while any API call is in flight. Works on EVERY screen
    // automatically (no per-page wiring). SharedDataService.loaderCount tracks
    // concurrent requests, so it stays visible until the last one settles.
    // Requests that opt out via SKIP_LOADER (e.g. a search with its own button
    // loader) are excluded — captured once so show/hide stay balanced.
    const skipLoader = request.context.get(SKIP_LOADER);
    if (!skipLoader) {
      this.sharedDataService.showLoader();
    }


    return from(this.preProcessRequest(request)).pipe(
      switchMap((processedRequest) =>
        next.handle(processedRequest).pipe(
          tap({
            next: (event: HttpEvent<any>) => {
              if (event instanceof HttpResponse) {
                if (
                  event?.body?.message === DEFAULT_INVALID_TOKEN_SERVER_RESPONSE ||
                  event?.body?.message === DEFAULT_INVALID_TOKEN_SIGNATURE_SERVER_RESPONSE ||
                  event?.status === 401 ||
                  event?.body?.status === 401
                ) {
                  this.helperService.refreshFlow()
                }
              }
            },
            error: (err: any) => {
              if (err instanceof HttpErrorResponse) {
                console.log(err);
                let msg = 'We are currently facing trouble in serving your request. Please try again in sometime.';
                let title = "We’re experiencing a hiccup!";
                let image = IMAGES_URL + 'handle-errors-icon-system.svg';
                if(err?.error?.status?.message?.details)
                {
                  msg = err?.error?.status?.message?.details;
                  title = "System Error"; 
                }
                const toastStrategy = request.context.get(TOAST_STRATEGY);
                const shouldSkipToast = toastStrategy === 'skip' || (toastStrategy === 'auto' && request.url.includes('authenticate'));
                if (!shouldSkipToast) { 
                  let dialogData = {
                    "image" : image,
                    "mainClass" : "dashboard-error-msg",
                    "title" : title,
                    "message" : msg + `<p>Please contact customer support for any further assistance at <a class="view-text-redirect" href="mailto:${environment.email}">${environment.email}</a> or, feel free to contact us at <a class="view-text-redirect" href="tel:${environment.phoneNumberToShow}">${environment.phoneNumberToShow}</a></p>`,
                    "btnText" : "Close",
                    "imageWidth" : 128,
                    "imageHeight" : 128,
                  };
                  this.errorService.showErrorDialog(dialogData);
                }
                if (err.status === 401) {
                  this.helperService.refreshFlow()
                }
              }
            },
          })
        )
      ),
      // Outer finalize so it always runs (even if token pre-processing rejects).
      finalize(() => {
        if (!skipLoader) {
          this.sharedDataService.hideLoader();
        }
      })
    );
  }

  // 👇 This handles the async token flow
  private async preProcessRequest(
    request: HttpRequest<any>
  ): Promise<HttpRequest<any>> {
    // if (!request.url.includes('assets') && !request.url.includes('authenticate')) {
    //   await this.helperService.tokenflow();
    // }
    return request;
  }
}
